var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { NgZone } from '@angular/core';
import { enterZone, leaveZone } from '@freelancer/time-utils';
import { Actions, Effect } from '@ngrx/effects';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, mergeAll, mergeMap, observeOn, publishReplay, refCount, startWith, tap, withLatestFrom, } from 'rxjs/operators';
import { isRequestDataAction, } from '../actions';
import { StoreBackend } from '../backend';
import { chunk, flatMap } from '../helpers';
import { slidingBufferTime } from '../operators/slidingBufferTime';
import { RequestStatusHandler } from '../request-status-handler.service';
import { referencesEqual } from '../store.helpers';
import { batchRequests } from './operators/batchRequests';
import { dedupeRequests } from './operators/dedupeRequests';
import { RequestDataConfig } from './request-data.interface';
var RequestDataEffect = /** @class */ (function () {
    function RequestDataEffect(storeBackend, actions$, ngZone, requestStatusHandler, config) {
        var _this = this;
        this.storeBackend = storeBackend;
        this.actions$ = actions$;
        this.ngZone = ngZone;
        this.requestStatusHandler = requestStatusHandler;
        this.config = config;
        var _a = this.config, scheduler = _a.scheduler, dedupeWindowTime = _a.dedupeWindowTime, batchWindowTime = _a.batchWindowTime;
        // Optimisation: dedupe requests with limit smaller than the largest in window
        var allRequests$ = this.actions$.pipe(filter(isRequestDataAction), map(function (action) { return action.payload; }));
        var requestErrors$ = this.requestStatusHandler.statusStream$.pipe(filter(function (_a) {
            var statusObject = _a.statusObject;
            return statusObject.error !== undefined;
        }));
        var allDedupedRequests$ = Rx.combineLatest(allRequests$.pipe(dedupeRequests(dedupeWindowTime, requestErrors$.pipe(map(function (e) { return e.request; })), leaveZone(this.ngZone, scheduler))), requestErrors$.pipe(withLatestFrom(Rx.combineLatest(
        // sliding buffer of failed requests clientRequestIds
        requestErrors$.pipe(map(function (_a) {
            var request = _a.request;
            return request;
        }), slidingBufferTime(dedupeWindowTime, 
        // this is a bit of a hack, but not leaving the NgZone when the
        // dedupeWindowTime is null enables the unit tests to work
        dedupeWindowTime
            ? leaveZone(this.ngZone, scheduler)
            : undefined), map(function (requestIdErrorBuffer) {
            return flatMap(requestIdErrorBuffer, function (_a) {
                var clientRequestIds = _a.clientRequestIds;
                return clientRequestIds;
            });
        })), 
        // sliding buffer of all requests
        allRequests$.pipe(slidingBufferTime(dedupeWindowTime, 
        // this is a bit of a hack, but not leaving the NgZone when the
        // dedupeWindowTime is null enables the unit tests to work
        dedupeWindowTime
            ? leaveZone(this.ngZone, scheduler)
            : undefined)))), tap(function (_a) {
            var _b = _a[0], request = _b.request, statusObject = _b.statusObject, _c = _a[1], requestIdErrorBuffer = _c[0], requestBuffer = _c[1];
            return requestBuffer
                .filter(function (r) {
                // has a previous request (r) been made to the same ref
                return referencesEqual(r.ref, request.ref) &&
                    // has none of the current request (request) clientRequestIds resulted in a previous error
                    !r.clientRequestIds.some(function (id) {
                        return requestIdErrorBuffer.includes(id);
                    });
            })
                .forEach(function (r) { return _this.requestStatusHandler.update(r, statusObject); });
        }), mapTo(undefined), startWith(undefined))).pipe(map(function (_a) {
            var source = _a[0];
            return source;
        }), distinctUntilChanged(), publishReplay(1), refCount());
        var plainRequests$ = allDedupedRequests$.pipe(filter(this.isPlainRequest));
        var queriedRequests$ = allDedupedRequests$.pipe(filter(function (request) { return !_this.isPlainRequest(request); }));
        // Batching only for plain requests (no query)
        // TODO: Address queried requests, i.e. regular datastore.list() calls
        this.requestData$ = Rx.merge(plainRequests$.pipe(batchRequests(batchWindowTime, leaveZone(this.ngZone, scheduler)), map(function (requests) {
            return flatMap(requests, function (request) {
                return splitRequestsOnId(request, _this.storeBackend.batchSize(request.ref));
            });
        }), mergeAll(), observeOn(enterZone(this.ngZone, scheduler))), queriedRequests$).pipe(mergeMap(function (request) {
            return _this.storeBackend
                .fetch(request.ref)
                .pipe(map(function (res) { return ({ res: res, request: request }); }));
        }), map(function (_a) {
            var res = _a.res, request = _a.request;
            return _this.onResponse(res, request);
        }), filter(function (action) { return action !== undefined; }));
    }
    RequestDataEffect.prototype.onResponse = function (response, request) {
        var order = request.ref.order ||
            this.storeBackend.defaultOrder(request.ref.path.collection);
        switch (response.status) {
            case 'success': {
                var action = {
                    type: 'API_FETCH_SUCCESS',
                    payload: {
                        type: request.ref.path.collection,
                        result: response.result,
                        ref: request.ref,
                        order: order,
                    },
                };
                return action;
            }
            default: {
                this.requestStatusHandler.update(request, {
                    ready: false,
                    error: response,
                });
                var action = {
                    type: 'API_FETCH_ERROR',
                    payload: {
                        type: request.ref.path.collection,
                        ref: request.ref,
                        order: order,
                    },
                };
                return action;
            }
        }
    };
    RequestDataEffect.prototype.isPlainRequest = function (request) {
        return request.ref.query === undefined;
    };
    __decorate([
        Effect(),
        __metadata("design:type", Rx.Observable)
    ], RequestDataEffect.prototype, "requestData$", void 0);
    return RequestDataEffect;
}());
export { RequestDataEffect };
function splitRequestsOnId(request, size) {
    var ids = request.ref.path.ids;
    if (!ids) {
        return [request];
    }
    return chunk(ids, size).map(function (chunkedIds) { return (__assign({}, request, { ref: __assign({}, request.ref, { path: __assign({}, request.ref.path, { ids: chunkedIds }) }) })); });
}
